/**
 * +grid: node art for the Waystone Grid (docs/design/waystone-grid.md "UI"). Each star is a painted
 * icon (gridIcons.ts) clipped inside a procedural frame drawn per node type, graded by state
 * (unreached = desaturated and dark, within reach = brighter, planned = cyan, allocated = full colour
 * with a gold frame and glow). Frames are rendered once per (icon, type, look, size step) into small
 * offscreen sprites, so a frame of the grid is a few hundred drawImage calls. Painted icons load
 * lazily (64 px first, 128 px when a star is drawn large) only while the grid screen is open.
 */
import { BASE_CLASSES, type GridNode, type GridNodeType } from '@keystone/content';
import { CLASS_EMBLEM } from '../kmmo/icons';
import { GRID_PAINTED, gridGlyph, gridIconKey, gridIconUrl, slotBadge } from './gridIcons';

/** How a star is graded (derived from its NodeState). */
export type Look = 'lit' | 'plan' | 'avail' | 'idle' | 'dormant' | 'sealed' | 'openable' | 'needkey' | 'open';

/** Frame radius in world units (min link distance is 32). */
export const WORLD_R: Readonly<Record<GridNodeType, number>> = { minor: 7.5, slot: 9, utility: 8.5, notable: 13, keystone: 20, lock: 12, gate: 18, dormant: 10 };
/** Smallest on-screen radius (css px) when zoomed far out, so every star stays legible. */
const MIN_PX: Readonly<Record<GridNodeType, number>> = { minor: 3, slot: 3.6, utility: 3.4, notable: 6.5, keystone: 11, lock: 5.5, gate: 9, dormant: 4 };
/** Sprite half-size as a multiple of the frame radius (room for spikes and glow). */
const PAD: Readonly<Record<GridNodeType, number>> = { minor: 1.75, slot: 1.75, utility: 1.75, notable: 1.95, keystone: 2.1, lock: 1.6, gate: 2.1, dormant: 1.3 };

/** Frame radius that fits a star's frame (spikes included, glow may clip) inside a `box` css px square. */
const FIT: Readonly<Record<GridNodeType, number>> = { minor: 1.25, slot: 1.3, utility: 1.38, notable: 1.48, keystone: 1.62, lock: 1.2, gate: 1.45, dormant: 1.1 };
export const fitRadius = (n: GridNode, box: number): number => box / 2 / FIT[n.type];

export const screenRadius = (n: GridNode, zoom: number): number => Math.max(MIN_PX[n.type], WORLD_R[n.type] * zoom);

const CLASS_COLOR: Readonly<Record<string, string>> = Object.fromEntries(BASE_CLASSES.map((c) => [c.id, c.color]));
export const THEME_COLOR: Readonly<Record<string, string>> = {
  might: '#e0785a',
  finesse: '#9a8cff',
  arcana: '#7fb8ff',
  endurance: '#9ed27a',
  precision: '#6fe0c8',
  hearth: '#a9c1d9',
};

// ---- metals -------------------------------------------------------------------
/** [highlight, light, mid, dark, shadow] */
type Metal = readonly [string, string, string, string, string];
const METAL = {
  bronze: ['#f3dcae', '#c49a5c', '#7a5a2c', '#4a3418', '#24180a'],
  steel: ['#eef2f8', '#a8b1c2', '#646d80', '#363c4a', '#181b22'],
  violet: ['#f1e8ff', '#b3a3e6', '#6a5c9c', '#3a3260', '#1c1830'],
  greengold: ['#f4f2bc', '#c4cc7c', '#7a8a40', '#42502a', '#1e2812'],
  gold: ['#fff6d2', '#ffd36e', '#c08a2c', '#7a5014', '#3a2406'],
  darkgold: ['#e9d29a', '#b48c48', '#76582a', '#463214', '#201608'],
  stone: ['#c4c0d2', '#8a849e', '#57526a', '#34304a', '#17151f'],
  cyan: ['#f0fdff', '#9ff0ff', '#48a6c4', '#1f5a70', '#0c2a36'],
} as const satisfies Record<string, Metal>;

function metalGrad(c: CanvasRenderingContext2D, x: number, y: number, r: number, m: Metal): CanvasGradient {
  const g = c.createLinearGradient(x - r, y - r, x + r, y + r);
  g.addColorStop(0, m[0]);
  g.addColorStop(0.28, m[1]);
  g.addColorStop(0.5, m[3]);
  g.addColorStop(0.68, m[1]);
  g.addColorStop(1, m[4]);
  return g;
}

/** Frame metal for a type in a look. */
function metalFor(type: GridNodeType, look: Look): Metal {
  if (look === 'lit' || look === 'open') return METAL.gold;
  if (look === 'plan') return METAL.cyan;
  if (look === 'dormant') return METAL.stone;
  switch (type) {
    case 'slot':
      return METAL.violet;
    case 'utility':
      return METAL.greengold;
    case 'notable':
    case 'keystone':
      return METAL.darkgold;
    case 'lock':
      return METAL.stone;
    case 'gate':
      return METAL.steel;
    default:
      return METAL.bronze;
  }
}

// ---- images -----------------------------------------------------------------------
interface Img {
  el: HTMLImageElement;
  ok: boolean;
}
const images = new Map<string, Img>();
let imageGen = 0;
const listeners = new Set<() => void>();

function want(key: string, size: 64 | 128): Img | null {
  const url = gridIconUrl(key, size);
  if (!url || typeof Image === 'undefined') return null;
  const id = `${key}@${size}`;
  let im = images.get(id);
  if (!im) {
    const el = new Image();
    im = { el, ok: false };
    images.set(id, im);
    const rec = im;
    el.decoding = 'async';
    el.onload = () => {
      rec.ok = true;
      imageGen++;
      for (const f of listeners) f();
    };
    el.src = url;
  }
  return im;
}

/** The best loaded image for a key at a device-pixel diameter (asks for 128 px when drawn large). */
function iconImage(key: string, diameter: number): HTMLImageElement | null {
  if (!GRID_PAINTED.has(key)) return null;
  const small = want(key, 64);
  if (diameter > 72) {
    const big = want(key, 128);
    if (big?.ok) return big.el;
  }
  return small?.ok ? small.el : null;
}

// ---- paths --------------------------------------------------------------------------
const pathCache = new Map<string, Path2D>();
function path2d(d: string): Path2D | null {
  if (typeof Path2D === 'undefined') return null;
  let p = pathCache.get(d);
  if (!p) pathCache.set(d, (p = new Path2D(d)));
  return p;
}

/** A class emblem (24×24 SVG markup) as path parts: { d, filled }. */
function emblemParts(classId: string): { d: string; filled: boolean }[] {
  const svg = CLASS_EMBLEM[classId] ?? '';
  const out: { d: string; filled: boolean }[] = [];
  for (const m of svg.matchAll(/<(path|circle)\b([^>]*)\/?>/g)) {
    const attrs = m[2]!;
    const filled = attrs.includes('fill-opacity');
    if (m[1] === 'path') {
      const d = /\bd="([^"]+)"/.exec(attrs)?.[1];
      if (d) out.push({ d, filled });
    } else {
      const num = (a: string): number => Number(new RegExp(`\\b${a}="([^"]+)"`).exec(attrs)?.[1] ?? 0);
      const cx = num('cx');
      const cy = num('cy');
      const r = num('r');
      out.push({ d: `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0z`, filled });
    }
  }
  return out;
}

// ---- shapes ----------------------------------------------------------------------------
function polygon(c: CanvasRenderingContext2D, x: number, y: number, r: number, sides: number, rot: number): void {
  for (let i = 0; i < sides; i++) {
    const a = rot + (Math.PI * 2 * i) / sides;
    const px = x + Math.cos(a) * r;
    const py = y + Math.sin(a) * r;
    if (i === 0) c.moveTo(px, py);
    else c.lineTo(px, py);
  }
  c.closePath();
}

const HEX_ROT = -Math.PI / 2;

/** A metal ring (annulus) with a bevel: light outer edge on top, dark inner edge. */
function ring(c: CanvasRenderingContext2D, x: number, y: number, r: number, wdt: number, m: Metal, sides = 0): void {
  c.beginPath();
  if (sides) {
    polygon(c, x, y, r, sides, HEX_ROT);
    polygon(c, x, y, r - wdt * 1.15, sides, HEX_ROT);
  } else {
    c.arc(x, y, r, 0, Math.PI * 2);
    c.arc(x, y, r - wdt, 0, Math.PI * 2, true);
  }
  c.fillStyle = metalGrad(c, x, y, r, m);
  c.fill('evenodd');
  const lw = Math.max(0.6, wdt * 0.16);
  c.lineWidth = lw;
  c.strokeStyle = 'rgba(0, 0, 0, 0.55)';
  c.beginPath();
  if (sides) polygon(c, x, y, r - wdt * 1.15, sides, HEX_ROT);
  else c.arc(x, y, r - wdt, 0, Math.PI * 2);
  c.stroke();
  c.strokeStyle = 'rgba(0, 0, 0, 0.6)';
  c.beginPath();
  if (sides) polygon(c, x, y, r, sides, HEX_ROT);
  else c.arc(x, y, r, 0, Math.PI * 2);
  c.stroke();
  if (!sides) {
    c.strokeStyle = hexA(m[0], 0.55);
    c.beginPath();
    c.arc(x, y, r - lw, Math.PI * 1.05, Math.PI * 1.7);
    c.stroke();
  }
}

/** Fill the clip with the icon (or the vector glyph), graded for the look. */
function iconFill(c: CanvasRenderingContext2D, key: string, x: number, y: number, ri: number, look: Look, theme: string): void {
  const img = iconImage(key, ri * 2);
  if (img) {
    const s = ri * 2.2;
    c.drawImage(img, x - s / 2, y - s / 2, s, s);
  } else {
    const bg = c.createRadialGradient(x, y - ri * 0.3, 0, x, y, ri);
    bg.addColorStop(0, hexA(theme, 0.55));
    bg.addColorStop(1, 'rgba(8, 10, 24, 1)');
    c.fillStyle = bg;
    c.fillRect(x - ri, y - ri, ri * 2, ri * 2);
    const p = path2d(gridGlyph(key));
    if (p) {
      c.save();
      const k = (ri * 1.5) / 24;
      c.translate(x - 12 * k, y - 12 * k);
      c.scale(k, k);
      c.lineJoin = 'round';
      c.lineCap = 'round';
      c.fillStyle = 'rgba(255, 245, 225, 0.18)';
      c.fill(p);
      c.strokeStyle = '#fff4dc';
      c.lineWidth = Math.max(1.6, 1.2 / k);
      c.stroke(p);
      c.restore();
    }
  }
  // Grade: desaturate and darken unallocated art; cyan wash for planned.
  const grade: Record<Look, [number, number]> = {
    lit: [0, 0],
    open: [0, 0],
    plan: [0.35, 0.05],
    avail: [0.45, 0.12],
    idle: [0.72, 0.3],
    sealed: [0.85, 0.45],
    needkey: [0.8, 0.45],
    openable: [0.4, 0.15],
    dormant: [1, 0.68],
  };
  const [sat, dark] = grade[look];
  if (sat > 0) {
    c.globalCompositeOperation = 'saturation';
    c.globalAlpha = sat;
    c.fillStyle = '#808080';
    c.fillRect(x - ri, y - ri, ri * 2, ri * 2);
    c.globalCompositeOperation = 'source-over';
    c.globalAlpha = 1;
  }
  if (dark > 0) {
    c.fillStyle = `rgba(4, 6, 16, ${dark})`;
    c.fillRect(x - ri, y - ri, ri * 2, ri * 2);
  }
  if (look === 'plan') {
    c.fillStyle = 'rgba(120, 225, 255, 0.16)';
    c.fillRect(x - ri, y - ri, ri * 2, ri * 2);
  }
  // Inner shadow so the art sits inside the frame.
  const sh = c.createRadialGradient(x, y, ri * 0.55, x, y, ri);
  sh.addColorStop(0, 'rgba(0, 0, 0, 0)');
  sh.addColorStop(1, 'rgba(0, 0, 0, 0.55)');
  c.fillStyle = sh;
  c.fillRect(x - ri, y - ri, ri * 2, ri * 2);
}

function glow(c: CanvasRenderingContext2D, x: number, y: number, r0: number, r1: number, rgb: string, a: number): void {
  const g = c.createRadialGradient(x, y, r0, x, y, r1);
  g.addColorStop(0, `rgba(${rgb}, ${a})`);
  g.addColorStop(1, `rgba(${rgb}, 0)`);
  c.fillStyle = g;
  c.beginPath();
  c.arc(x, y, r1, 0, Math.PI * 2);
  c.fill();
}

function clipCircle(c: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  c.beginPath();
  c.arc(x, y, r, 0, Math.PI * 2);
  c.clip();
}

/** Spiky filigree behind a notable / keystone: `n` kite spikes alternating long / short. */
function spikes(c: CanvasRenderingContext2D, x: number, y: number, rIn: number, rLong: number, rShort: number, n: number, halfW: number, m: Metal, rot: number): void {
  c.beginPath();
  for (let i = 0; i < n; i++) {
    const a = rot + (Math.PI * 2 * i) / n;
    const len = i % 2 ? rShort : rLong;
    const w = i % 2 ? halfW * 0.7 : halfW;
    c.moveTo(x + Math.cos(a - w) * rIn, y + Math.sin(a - w) * rIn);
    c.lineTo(x + Math.cos(a) * len, y + Math.sin(a) * len);
    c.lineTo(x + Math.cos(a + w) * rIn, y + Math.sin(a + w) * rIn);
  }
  c.closePath();
  c.fillStyle = metalGrad(c, x, y, rLong, m);
  c.fill();
  c.lineWidth = Math.max(0.6, rIn * 0.04);
  c.strokeStyle = 'rgba(0, 0, 0, 0.6)';
  c.stroke();
}

// ---- the sprite painter --------------------------------------------------------------------
/** Paint a star centred at (x, y) with frame radius r (device px) onto `c`. Static parts only. */
function paintStar(c: CanvasRenderingContext2D, n: GridNode, key: string, look: Look, x: number, y: number, r: number): void {
  const theme = THEME_COLOR[n.theme] ?? '#9fb0ff';
  const m = metalFor(n.type, look);
  const lit = look === 'lit' || look === 'open';
  // Glow
  if (lit) glow(c, x, y, r * 0.6, r * (n.type === 'keystone' ? 2.05 : n.type === 'notable' || n.type === 'gate' ? 1.9 : 1.7), '255, 196, 80', n.type === 'minor' ? 0.5 : 0.6);
  else if (look === 'plan') glow(c, x, y, r * 0.6, r * 1.7, '120, 225, 255', 0.45);
  else if (n.type === 'keystone') glow(c, x, y, r * 0.6, r * 2.05, '255, 240, 210', look === 'avail' ? 0.3 : 0.16);
  else if (n.type === 'gate') glow(c, x, y, r * 0.5, r * 2, hexRgb(CLASS_COLOR[n.gate ?? ''] ?? '#8899ff'), 0.35);
  else if (look === 'openable') glow(c, x, y, r * 0.6, r * 1.6, '255, 210, 120', 0.3);

  switch (n.type) {
    case 'gate': {
      const col = CLASS_COLOR[n.gate ?? ''] ?? '#8899ff';
      // Constellation rays
      c.strokeStyle = lit ? 'rgba(255, 225, 150, 0.55)' : hexA(col, 0.5);
      c.lineWidth = Math.max(0.8, r * 0.05);
      c.beginPath();
      for (let i = 0; i < 8; i++) {
        const a = (Math.PI * i) / 4 + Math.PI / 8;
        c.moveTo(x + Math.cos(a) * r * 1.15, y + Math.sin(a) * r * 1.15);
        c.lineTo(x + Math.cos(a) * r * (i % 2 ? 1.5 : 1.95), y + Math.sin(a) * r * (i % 2 ? 1.5 : 1.95));
      }
      c.stroke();
      // Cardinal points
      c.beginPath();
      for (let i = 0; i < 4; i++) {
        const a = (Math.PI / 2) * i - Math.PI / 2;
        const tip = r * 1.38;
        c.moveTo(x + Math.cos(a - 0.2) * r * 0.95, y + Math.sin(a - 0.2) * r * 0.95);
        c.lineTo(x + Math.cos(a) * tip, y + Math.sin(a) * tip);
        c.lineTo(x + Math.cos(a + 0.2) * r * 0.95, y + Math.sin(a + 0.2) * r * 0.95);
      }
      c.fillStyle = metalGrad(c, x, y, r * 1.4, m);
      c.fill();
      // Disc
      const d = c.createRadialGradient(x, y - r * 0.3, 0, x, y, r);
      d.addColorStop(0, hexA(col, 0.95));
      d.addColorStop(0.6, hexA(col, 0.35));
      d.addColorStop(1, 'rgba(6, 8, 20, 1)');
      c.fillStyle = d;
      c.beginPath();
      c.arc(x, y, r * 0.86, 0, Math.PI * 2);
      c.fill();
      // Emblem
      const k = (r * 1.15) / 24;
      c.save();
      c.translate(x - 12 * k, y - 12 * k);
      c.scale(k, k);
      c.lineJoin = 'round';
      c.lineCap = 'round';
      c.lineWidth = Math.max(1.7, 1.6 / k);
      for (const part of emblemParts(n.gate ?? '')) {
        const p = path2d(part.d);
        if (!p) continue;
        if (part.filled) {
          c.fillStyle = lit ? 'rgba(255, 236, 180, 0.45)' : 'rgba(240, 245, 255, 0.3)';
          c.fill(p);
        }
        c.strokeStyle = lit ? '#fff1c2' : '#f4f6ff';
        c.stroke(p);
      }
      c.restore();
      ring(c, x, y, r, r * 0.16, m);
      ring(c, x, y, r * 0.86, r * 0.05, m);
      return;
    }
    case 'lock': {
      const open = look === 'open';
      const d = c.createRadialGradient(x, y, 0, x, y, r);
      d.addColorStop(0, open ? 'rgba(90, 64, 20, 1)' : 'rgba(46, 34, 80, 1)');
      d.addColorStop(1, 'rgba(10, 8, 20, 1)');
      c.fillStyle = d;
      c.beginPath();
      c.arc(x, y, r * 0.75, 0, Math.PI * 2);
      c.fill();
      const stone = open ? METAL.gold : METAL.stone;
      ring(c, x, y, r, r * 0.27, stone);
      // Engraved runes on the ring
      c.strokeStyle = open ? 'rgba(90, 50, 10, 0.8)' : look === 'openable' ? 'rgba(255, 215, 140, 0.85)' : 'rgba(200, 180, 255, 0.55)';
      c.lineWidth = Math.max(0.6, r * 0.045);
      c.beginPath();
      for (let i = 0; i < 12; i++) {
        const a = (Math.PI * 2 * i) / 12;
        const r0 = r * 0.8;
        const r1 = r * 0.95;
        const ca = Math.cos(a);
        const sa = Math.sin(a);
        c.moveTo(x + ca * r0, y + sa * r0);
        c.lineTo(x + ca * r1, y + sa * r1);
        if (i % 3 === 0) {
          const b = a + 0.12;
          c.moveTo(x + ca * r1, y + sa * r1);
          c.lineTo(x + Math.cos(b) * r0, y + Math.sin(b) * r0);
        } else if (i % 3 === 1) {
          c.moveTo(x + Math.cos(a - 0.1) * (r0 + r1) / 2, y + Math.sin(a - 0.1) * (r0 + r1) / 2);
          c.lineTo(x + Math.cos(a + 0.1) * (r0 + r1) / 2, y + Math.sin(a + 0.1) * (r0 + r1) / 2);
        }
      }
      c.stroke();
      // Keyhole (sealed) or an open sigil
      const p = path2d(gridGlyph('lock'));
      if (open) {
        c.strokeStyle = '#ffe7a8';
        c.lineWidth = Math.max(1, r * 0.1);
        c.beginPath();
        c.arc(x, y, r * 0.38, -0.5, Math.PI * 1.35);
        c.stroke();
      } else if (p) {
        const k = (r * 1.25) / 24;
        c.save();
        c.translate(x - 12 * k, y - 12 * k);
        c.scale(k, k);
        const kg = c.createLinearGradient(0, 4, 0, 19);
        const hot = look === 'openable';
        kg.addColorStop(0, hot ? '#fff1c2' : look === 'needkey' ? '#d8c8ff' : '#9a88c8');
        kg.addColorStop(1, hot ? '#d08a2a' : look === 'needkey' ? '#6a58a8' : '#3c3264');
        c.fillStyle = kg;
        c.fill(p);
        c.strokeStyle = 'rgba(0, 0, 0, 0.7)';
        c.lineWidth = 0.8;
        c.stroke(p);
        c.restore();
      }
      return;
    }
    case 'keystone': {
      spikes(c, x, y, r * 0.95, r * 1.55, r * 1.3, 16, 0.13, m, -Math.PI / 2);
      ring(c, x, y, r * 1.08, r * 0.08, m);
      c.save();
      clipCircle(c, x, y, r * 0.84);
      iconFill(c, key, x, y, r * 0.84, look, theme);
      c.restore();
      ring(c, x, y, r, r * 0.17, m);
      // Gems at the cardinal points
      for (let i = 0; i < 4; i++) {
        const a = (Math.PI / 2) * i - Math.PI / 2;
        const gx = x + Math.cos(a) * r * 0.92;
        const gy = y + Math.sin(a) * r * 0.92;
        c.fillStyle = lit ? '#fff3c4' : '#cfd6ff';
        c.strokeStyle = 'rgba(0, 0, 0, 0.6)';
        c.lineWidth = Math.max(0.5, r * 0.02);
        c.beginPath();
        polygon(c, gx, gy, r * 0.09, 4, a);
        c.fill();
        c.stroke();
      }
      return;
    }
    case 'notable': {
      spikes(c, x, y, r * 0.9, r * 1.42, r * 1.2, 8, 0.2, m, -Math.PI / 2);
      c.save();
      clipCircle(c, x, y, r * 0.82);
      iconFill(c, key, x, y, r * 0.82, look, theme);
      c.restore();
      ring(c, x, y, r, r * 0.2, m);
      c.strokeStyle = hexA(m[1], 0.7);
      c.lineWidth = Math.max(0.5, r * 0.035);
      c.beginPath();
      c.arc(x, y, r * 0.88, 0, Math.PI * 2);
      c.stroke();
      return;
    }
    case 'slot': {
      c.save();
      c.beginPath();
      polygon(c, x, y, r * 0.86, 6, HEX_ROT);
      c.clip();
      iconFill(c, key, x, y, r * 0.86, look, theme);
      c.restore();
      ring(c, x, y, r * 1.05, r * 0.2, m, 6);
      const badge = slotBadge(n);
      if (badge && r >= 9) {
        const bx = x + r * 0.78;
        const by = y + r * 0.62;
        const br = Math.max(4, r * 0.34);
        c.fillStyle = 'rgba(14, 12, 30, 0.95)';
        c.strokeStyle = metalGrad(c, bx, by, br, m);
        c.lineWidth = Math.max(0.8, br * 0.22);
        c.beginPath();
        c.arc(bx, by, br, 0, Math.PI * 2);
        c.fill();
        c.stroke();
        c.fillStyle = lit ? '#ffe6a6' : '#e6deff';
        c.font = `700 ${Math.round(br * 1.25)}px system-ui, sans-serif`;
        c.textAlign = 'center';
        c.textBaseline = 'middle';
        c.fillText(badge, bx, by + br * 0.06);
      }
      return;
    }
    case 'utility': {
      // Four leaf nubs on the diagonals
      c.beginPath();
      for (let i = 0; i < 4; i++) {
        const a = (Math.PI / 2) * i + Math.PI / 4;
        const ca = Math.cos(a);
        const sa = Math.sin(a);
        c.moveTo(x + Math.cos(a - 0.32) * r * 0.9, y + Math.sin(a - 0.32) * r * 0.9);
        c.quadraticCurveTo(x + ca * r * 1.2 - sa * r * 0.18, y + sa * r * 1.2 + ca * r * 0.18, x + ca * r * 1.32, y + sa * r * 1.32);
        c.quadraticCurveTo(x + ca * r * 1.2 + sa * r * 0.18, y + sa * r * 1.2 - ca * r * 0.18, x + Math.cos(a + 0.32) * r * 0.9, y + Math.sin(a + 0.32) * r * 0.9);
      }
      c.fillStyle = metalGrad(c, x, y, r * 1.3, m);
      c.fill();
      c.save();
      clipCircle(c, x, y, r * 0.82);
      iconFill(c, key, x, y, r * 0.82, look, theme);
      c.restore();
      ring(c, x, y, r, r * 0.2, m);
      return;
    }
    case 'dormant': {
      c.save();
      c.globalAlpha = 0.55;
      clipCircle(c, x, y, r * 0.8);
      iconFill(c, key, x, y, r * 0.8, look, theme);
      c.restore();
      c.strokeStyle = 'rgba(160, 165, 200, 0.45)';
      c.lineWidth = Math.max(0.8, r * 0.1);
      c.setLineDash([r * 0.3, r * 0.25]);
      c.beginPath();
      c.arc(x, y, r * 0.92, 0, Math.PI * 2);
      c.stroke();
      c.setLineDash([]);
      return;
    }
    default: {
      c.save();
      clipCircle(c, x, y, r * 0.84);
      iconFill(c, key, x, y, r * 0.84, look, theme);
      c.restore();
      ring(c, x, y, r, Math.max(1, r * 0.2), m);
    }
  }
}

// ---- sprite cache -----------------------------------------------------------------------
const STEP = Math.log(1.1);

export class GridArt {
  private sprites = new Map<string, HTMLCanvasElement>();
  private gen = -1;
  private readonly keys = new Map<string, string>();
  private readonly onImage: () => void;

  constructor(onChange: () => void) {
    this.onImage = onChange;
    listeners.add(this.onImage);
  }

  /** Start loading every painted icon these stars use (64 px; 128 px follows on demand). */
  load(nodes: readonly GridNode[]): void {
    for (const n of nodes) {
      const k = this.key(n);
      if (GRID_PAINTED.has(k)) want(k, 64);
    }
  }

  key(n: GridNode): string {
    let k = this.keys.get(n.id);
    if (!k) this.keys.set(n.id, (k = gridIconKey(n)));
    return k;
  }

  /** The cached sprite for a star at device radius ~rDev (quantised to 10% steps). */
  sprite(n: GridNode, look: Look, rDev: number): { c: HTMLCanvasElement; r: number } {
    if (this.gen !== imageGen) {
      this.sprites.clear();
      this.gen = imageGen;
    }
    const q = Math.max(4, Math.round(Math.log(Math.max(2, rDev)) / STEP));
    const r = Math.exp(q * STEP);
    const key = this.key(n);
    const id = `${n.type}|${key}|${n.type === 'slot' ? slotBadge(n) : ''}|${look}|${q}`;
    let c = this.sprites.get(id);
    if (!c) {
      if (this.sprites.size > 2400) this.sprites.clear();
      c = document.createElement('canvas');
      const half = Math.ceil(r * PAD[n.type]) + 1;
      c.width = c.height = half * 2;
      const g = c.getContext('2d')!;
      paintStar(g, n, key, look, half, half, r);
      this.sprites.set(id, c);
    }
    return { c, r };
  }

  /** Draw a star centred at css (x, y) with css radius rCss on a context scaled by dpr. */
  draw(g: CanvasRenderingContext2D, n: GridNode, look: Look, x: number, y: number, rCss: number, dpr: number): void {
    const { c, r } = this.sprite(n, look, rCss * dpr);
    const k = (rCss * dpr) / r / dpr; // device sprite px → css px at the exact radius
    const s = c.width * k;
    g.drawImage(c, x - s / 2, y - s / 2, s, s);
  }

  dispose(): void {
    listeners.delete(this.onImage);
    this.sprites.clear();
  }
}

export function hexA(hex: string, a: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return `rgba(160, 175, 230, ${a})`;
  const v = parseInt(m[1]!, 16);
  return `rgba(${(v >> 16) & 255}, ${(v >> 8) & 255}, ${v & 255}, ${a})`;
}

function hexRgb(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '136, 153, 255';
  const v = parseInt(m[1]!, 16);
  return `${(v >> 16) & 255}, ${(v >> 8) & 255}, ${v & 255}`;
}
